import type * as React from "react";

/**
 * Compact, non-interactive content label (Wanted "Content Badge").
 * Ported from the Wanted Design System bundle (components/data-display/Tag.jsx).
 */
export interface TagProps {
  /** @default "neutral" */
  color?: "neutral" | "primary" | "positive" | "negative" | "cautionary" | "violet";
  /** @default "weak" */
  variant?: "weak" | "solid" | "outline";
  /** @default "medium" */
  size?: "medium" | "small";
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

const COLORS = {
  neutral: {
    fg: "var(--label-neutral)",
    weak: "var(--fill-normal)",
    solid: "var(--label-neutral)",
  },
  primary: {
    fg: "var(--primary-normal)",
    weak: "color-mix(in srgb, var(--primary-normal) 10%, transparent)",
    solid: "var(--primary-normal)",
  },
  positive: {
    fg: "var(--status-positive)",
    weak: "color-mix(in srgb, var(--status-positive) 10%, transparent)",
    solid: "var(--status-positive)",
  },
  negative: {
    fg: "var(--status-negative)",
    weak: "color-mix(in srgb, var(--status-negative) 10%, transparent)",
    solid: "var(--status-negative)",
  },
  cautionary: {
    fg: "var(--status-cautionary)",
    weak: "color-mix(in srgb, var(--status-cautionary) 12%, transparent)",
    solid: "var(--status-cautionary)",
  },
  violet: {
    fg: "var(--accent-foreground-violet)",
    weak: "var(--accent-background-violet)",
    solid: "var(--accent-foreground-violet)",
  },
} as const;

const SIZES = {
  medium: { h: 24, padX: 8, font: "var(--font-size-label-2)" },
  small: { h: 20, padX: 6, font: "var(--font-size-caption-1)" },
} as const;

export function Tag({
  children,
  color = "neutral",
  variant = "weak",
  size = "medium",
  style,
}: TagProps) {
  const c = COLORS[color] ?? COLORS.neutral;
  const s = SIZES[size] ?? SIZES.medium;
  const look: React.CSSProperties =
    variant === "solid"
      ? { background: c.solid, color: "var(--static-white)", boxShadow: "none" }
      : variant === "outline"
        ? { background: "transparent", color: c.fg, boxShadow: `inset 0 0 0 1px ${c.fg}` }
        : { background: c.weak, color: c.fg, boxShadow: "none" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: s.h,
        padding: `0 ${s.padX}px`,
        borderRadius: "var(--radius-xs)",
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
        ...look,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export default Tag;
